import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MultiSelect } from '../ui/multi-select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { cn } from '../ui/utils';
import { useData } from '../../context/DataContext';
import { learningApi } from '../../utils/learning-api';
import type {
    AssignmentPriority,
    AssignmentType,
    Course,
    LearningDepartmentOption,
} from '../../types/learning.types';

/**
 * Assign a Course (LEARNING.md §7.3) — the register's primary action.
 *
 * Modelled on `pages/policy-new-assign.tsx`: a course picker, an Individuals | Departments
 * segmented control, a due date and a priority. Targets are chosen with the shared
 * MultiSelect (searchable list) rather than a wall of chips — a tenant can have hundreds of
 * people, and the list has to stay usable.
 *
 * The people list comes from the same source the rest of the app uses (`useData().users`,
 * i.e. GET /api/users), filtered to active, non-archived people the way
 * `AddUsersToPolicyModal` filters it.
 *
 * Only Active courses can be assigned — the server refuses anything else, this stops a Draft
 * being offered in the first place.
 */

interface LearningAssignModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Pre-selected course when opened from a course context; the Select stays editable. */
    courseId?: number;
    onAssigned: () => void;
}

export const LearningAssignModal: React.FC<LearningAssignModalProps> = ({
    open,
    onOpenChange,
    courseId,
    onAssigned,
}) => {
    const { users } = useData();

    const [courses, setCourses] = useState<Course[]>([]);
    const [departments, setDepartments] = useState<LearningDepartmentOption[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);

    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [mode, setMode] = useState<AssignmentType>('Individual');
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState<AssignmentPriority>('Normal');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        setSelectedCourseId(courseId ? String(courseId) : '');
        setMode('Individual');
        setSelectedUserIds([]);
        setSelectedDepartments([]);
        setDueDate('');
        setPriority('Normal');
        setLoadingOptions(true);

        Promise.all([
            learningApi.courses.list({ status: 'Active' }),
            learningApi.assignments.list(),
        ])
            .then(([activeCourses, register]) => {
                if (cancelled) return;
                setCourses(activeCourses);
                setDepartments(register.options?.departments ?? []);
            })
            .catch((error) => console.error('Load assign options error:', error))
            .finally(() => {
                if (!cancelled) setLoadingOptions(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, courseId]);

    const people = useMemo(
        () => users.filter((user) => user.isActive && !user.archived),
        [users],
    );

    /**
     * MultiSelect keys its command items by LABEL, so two people whose name+role collide
     * would collapse into one item and a single click would select both. Only the colliding
     * rows get the email appended, so the common case stays short. Same treatment for
     * departments, whose names are not unique in the data either.
     */
    const personOptions = useMemo(() => {
        const base = people.map((user) => ({
            value: String(user.id),
            label: user.jobRole ? `${user.name} (${user.jobRole})` : user.name,
            email: user.email,
        }));
        const labelCounts = base.reduce<Record<string, number>>((acc, option) => {
            acc[option.label] = (acc[option.label] ?? 0) + 1;
            return acc;
        }, {});
        return base.map((option) =>
            labelCounts[option.label] > 1
                ? { value: option.value, label: `${option.label} — ${option.email}` }
                : { value: option.value, label: option.label },
        );
    }, [people]);

    const departmentOptions = useMemo(() => {
        const labelCounts = departments.reduce<Record<string, number>>((acc, department) => {
            acc[department.departmentName] = (acc[department.departmentName] ?? 0) + 1;
            return acc;
        }, {});
        return departments.map((department) => ({
            value: department.departmentCode,
            label: labelCounts[department.departmentName] > 1
                ? `${department.departmentName} (${department.departmentCode})`
                : department.departmentName,
        }));
    }, [departments]);

    const switchMode = (next: AssignmentType) => {
        if (next === mode) return;
        setMode(next);
    };

    const selectedCount =
        mode === 'Individual' ? selectedUserIds.length : selectedDepartments.length;

    const handleSubmit = async () => {
        if (!selectedCourseId) {
            toast.error('Pick a course to assign.');
            return;
        }
        if (mode === 'Individual' && selectedUserIds.length === 0) {
            toast.error('Pick at least one person.');
            return;
        }
        if (mode === 'Department' && selectedDepartments.length === 0) {
            toast.error('Pick at least one department.');
            return;
        }

        try {
            setSubmitting(true);
            const result = await learningApi.assignments.create({
                courseId: Number(selectedCourseId),
                mode,
                ...(mode === 'Individual'
                    ? { userIds: selectedUserIds }
                    : { departments: selectedDepartments }),
                dueDate: dueDate || null,
                priority,
            });
            const skippedNote = result.skipped
                ? `, ${result.skipped} already assigned`
                : '';
            toast.success(
                `Assigned ${result.created} learner${result.created === 1 ? '' : 's'}${skippedNote}`,
            );
            onAssigned();
            onOpenChange(false);
        } catch (error) {
            console.error('Assign course error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenChange = (next: boolean) => {
        if (submitting) return;
        onOpenChange(next);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BookOpen size={20} />
                        Assign a Course
                    </DialogTitle>
                    <DialogDescription>
                        Assign a published course to individuals or whole departments. People
                        who already have it are skipped rather than duplicated.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* ─── Course ─── */}
                    <div className="space-y-1.5">
                        <Label htmlFor="learning-assign-course">Course</Label>
                        <Select
                            value={selectedCourseId}
                            onValueChange={setSelectedCourseId}
                            disabled={loadingOptions}
                        >
                            <SelectTrigger id="learning-assign-course" className="w-full">
                                <SelectValue placeholder="Select an active course" />
                            </SelectTrigger>
                            <SelectContent>
                                {courses.map((course) => (
                                    <SelectItem key={course.id} value={String(course.id)}>
                                        {course.title}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {!loadingOptions && courses.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                No active courses are available to assign.
                            </p>
                        )}
                    </div>

                    {/* ─── Assign to ─── */}
                    <div className="space-y-2">
                        <Label>Assign to</Label>
                        <div className="inline-flex gap-1 rounded-full bg-muted p-1">
                            <Button
                                type="button"
                                variant={mode === 'Individual' ? 'secondary' : 'ghost'}
                                size="sm"
                                className={cn(
                                    'rounded-full',
                                    mode === 'Individual' && 'bg-card shadow-sm',
                                )}
                                onClick={() => switchMode('Individual')}
                            >
                                Individuals
                            </Button>
                            <Button
                                type="button"
                                variant={mode === 'Department' ? 'secondary' : 'ghost'}
                                size="sm"
                                className={cn(
                                    'rounded-full',
                                    mode === 'Department' && 'bg-card shadow-sm',
                                )}
                                onClick={() => switchMode('Department')}
                            >
                                Departments
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {mode === 'Individual'
                                ? 'Individual users'
                                : 'Departments (expanded to current members when published)'}
                        </p>

                        {mode === 'Individual' ? (
                            <MultiSelect
                                options={personOptions}
                                selected={selectedUserIds.map(String)}
                                onChange={(values) => setSelectedUserIds(values.map(Number))}
                                placeholder="Select people"
                                emptyMessage="No active people available."
                                maxDisplayTags={2}
                            />
                        ) : (
                            <MultiSelect
                                options={departmentOptions}
                                selected={selectedDepartments}
                                onChange={setSelectedDepartments}
                                placeholder="Select departments"
                                emptyMessage="No departments available."
                                maxDisplayTags={2}
                            />
                        )}
                        <p className="text-xs text-muted-foreground">
                            {selectedCount === 0
                                ? 'Nobody selected yet'
                                : mode === 'Individual'
                                    ? `${selectedCount} user${selectedCount === 1 ? '' : 's'} selected`
                                    : `${selectedCount} department${selectedCount === 1 ? '' : 's'} selected`}
                        </p>
                    </div>

                    {/* ─── Due date + priority ─── */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="learning-assign-due">Due date</Label>
                            <Input
                                id="learning-assign-due"
                                type="date"
                                value={dueDate}
                                onChange={(event) => setDueDate(event.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Priority</Label>
                            <Select
                                value={priority}
                                onValueChange={(value) =>
                                    setPriority(value === 'High' ? 'High' : 'Normal')
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Normal">Normal</SelectItem>
                                    <SelectItem value="High">High</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSubmit} disabled={submitting}>
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        Publish assignment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default LearningAssignModal;
